import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Package, Search } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const TABS = [
    { value: "low", label: "Low stock" },
    { value: "expiring", label: "Expiring soon" },
    { value: "batches", label: "All batches" },
];

const Content = () => {
    const [tab, setTab] = useState("low");
    const [lowStock, setLowStock] = useState([]);
    const [expiring, setExpiring] = useState([]);
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(60);
    const [threshold, setThreshold] = useState(10);

    const load = async () => {
        setLoading(true);
        try {
            if (tab === "low") {
                const r = await api.get("/admin/inventory/low-stock", { params: { threshold } });
                setLowStock(r.data.items);
            } else if (tab === "expiring") {
                const r = await api.get("/admin/inventory/expiring", { params: { days } });
                setExpiring(r.data.items);
            } else {
                const r = await api.get("/admin/inventory/batches", { params: { page_size: 50 } });
                setBatches(r.data.items);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, days, threshold]);

    return (
        <div className="space-y-5" data-testid="admin-inventory-page">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                {TABS.map((t) => (
                    <button
                        key={t.value}
                        onClick={() => setTab(t.value)}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            tab === t.value ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid={`inventory-tab-${t.value}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "low" && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="inventory-low-stock">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-900">Low-stock products</h3>
                        <label className="text-xs text-slate-500">
                            Threshold:
                            <input type="number" value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value || "10", 10))} min="0" max="100" className="ml-2 w-16 rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid="inventory-threshold" />
                        </label>
                    </div>
                    <table className="mt-4 min-w-full divide-y divide-slate-100 text-sm">
                        <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Stock</th><th className="px-3 py-2 text-right">Category</th><th /></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? <tr><td colSpan={4} className="px-3 py-6"><div className="h-6 skeleton rounded" /></td></tr> :
                              lowStock.length === 0 ? <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Everything stocked above threshold.</td></tr> :
                              lowStock.map((p) => (
                                <tr key={p.id}>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-2.5">
                                            <img src={(p.images || [])[0] || "/pills-logo.jpg"} alt="" className="h-7 w-7 rounded object-cover ring-1 ring-slate-200" />
                                            <div>
                                                <p className="line-clamp-1 font-medium text-slate-900">{p.name}</p>
                                                <p className="text-xs text-slate-500">{p.brand}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
                                            p.stock_qty <= 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                                        }`}><AlertTriangle className="h-3 w-3" /> {p.stock_qty}</span>
                                    </td>
                                    <td className="px-3 py-2.5 text-right text-xs text-slate-500">{p.category_slug}</td>
                                    <td className="px-3 py-2.5 text-right">
                                        <Link to={`/admin/products?q=${encodeURIComponent(p.name)}`} className="text-xs font-medium text-brand-700 hover:underline">Restock</Link>
                                    </td>
                                </tr>
                              ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "expiring" && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="inventory-expiring">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-900">Expiring batches</h3>
                        <label className="text-xs text-slate-500">
                            Within:
                            <input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value || "60", 10))} min="1" max="365" className="ml-2 w-16 rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid="inventory-days" /> days
                        </label>
                    </div>
                    <table className="mt-4 min-w-full divide-y divide-slate-100 text-sm">
                        <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-left">Batch</th><th className="px-3 py-2 text-right">Qty left</th><th className="px-3 py-2 text-right">Expires</th></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? <tr><td colSpan={4} className="px-3 py-6"><div className="h-6 skeleton rounded" /></td></tr> :
                              expiring.length === 0 ? <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No batches expiring soon.</td></tr> :
                              expiring.map((b) => (
                                <tr key={b.id}>
                                    <td className="px-3 py-2.5 font-medium text-slate-900">{b.product_name}</td>
                                    <td className="px-3 py-2.5 text-slate-700">{b.batch_no}</td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">{b.qty_remaining}</td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                            b.is_expired ? "bg-rose-100 text-rose-700" :
                                            (b.days_left || 0) < 30 ? "bg-amber-100 text-amber-700" :
                                            "bg-emerald-50 text-emerald-700"
                                        }`}><Clock className="h-3 w-3" /> {b.expiry_date} {b.days_left != null && `(${b.days_left}d)`}</span>
                                    </td>
                                </tr>
                              ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "batches" && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="inventory-batches">
                    <h3 className="text-sm font-semibold text-slate-900">Recent batches received</h3>
                    {loading ? <div className="mt-4 h-24 skeleton rounded" /> : batches.length === 0 ? (
                        <p className="mt-6 text-center text-sm text-slate-400">No batches recorded yet. Use the "Restock" button on products to log one.</p>
                    ) : (
                        <table className="mt-4 min-w-full divide-y divide-slate-100 text-sm">
                            <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-left">Batch</th><th className="px-3 py-2 text-left">Supplier</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Expiry</th><th className="px-3 py-2 text-right">Received</th></tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {batches.map((b) => (
                                    <tr key={b.id}>
                                        <td className="px-3 py-2.5 font-medium text-slate-900">{b.product_name}</td>
                                        <td className="px-3 py-2.5 text-slate-700">{b.batch_no}</td>
                                        <td className="px-3 py-2.5 text-slate-700">{b.supplier}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums">{b.qty_remaining}/{b.qty_initial}</td>
                                        <td className="px-3 py-2.5 text-right text-slate-700">{b.expiry_date}</td>
                                        <td className="px-3 py-2.5 text-right text-xs text-slate-500">{new Date(b.received_at).toLocaleDateString("en-IN")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

const AdminInventory = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="Inventory" subtitle="Stock health, batches and expiry alerts">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminInventory;
